import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';

interface DateFilterContextType {
  getSelectedValue: (facetKey: string) => string;
  setSelectedValue: (facetKey: string, value: string) => void;
  getSelectedValueLabel: (facetKey: string) => string;
  setSelectedValueLabel: (facetKey: string, label: string) => void;
}

const DateFilterContext = createContext<DateFilterContextType | undefined>(undefined);

export function useDateFilter(facetKey: string | undefined | null) {
  const context = useContext(DateFilterContext);
  if (context === undefined) {
    throw new Error('useDateFilter must be used within a PortalDateFilterProvider');
  }

  // Handle undefined/null facetKey gracefully
  if (!facetKey) {
    return {
      selectedValue: '',
      setSelectedValue: () => {},
      selectedValueLabel: 'Any',
      setSelectedValueLabel: () => {},
    };
  }

  return {
    selectedValue: context.getSelectedValue(facetKey),
    setSelectedValue: (value: string) => context.setSelectedValue(facetKey, value),
    selectedValueLabel: context.getSelectedValueLabel(facetKey),
    setSelectedValueLabel: (label: string) => context.setSelectedValueLabel(facetKey, label),
  };
}

interface PortalDateFilterProviderProps {
  readonly children: ReactNode;
}

export function PortalDateFilterProvider({ children }: PortalDateFilterProviderProps) {
  const [selectedValues, setSelectedValues] = useState<Record<string, string>>({});
  const [selectedValueLabels, setSelectedValueLabels] = useState<Record<string, string>>({});

  const contextValue = useMemo(
    () => ({
      getSelectedValue: (facetKey: string) => selectedValues[facetKey] || '',
      setSelectedValue: (facetKey: string, value: string) => {
        setSelectedValues(prev => ({ ...prev, [facetKey]: value }));
      },
      getSelectedValueLabel: (facetKey: string) => selectedValueLabels[facetKey] || 'Any',
      setSelectedValueLabel: (facetKey: string, label: string) => {
        setSelectedValueLabels(prev => ({ ...prev, [facetKey]: label }));
      },
    }),
    [selectedValues, selectedValueLabels]
  );

  return <DateFilterContext.Provider value={contextValue}>{children}</DateFilterContext.Provider>;
}
